// RollBOT — AI Analyst core
// Versi upgrade: 6 metrik analisis, hill repeat data, badge, posisi tim, output per-role
// Engine: Google Gemini API (free tier)
// Untuk ganti ke Claude API nanti: cari "GANTI_KE_CLAUDE" di file ini

const GEMINI_MODEL = "gemini-1.5-flash";
const GEMINI_API_URL = (apiKey) =>
  `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent?key=${apiKey}`;

// API key diambil dari .env — REACT_APP_GEMINI_API_KEY
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY || "";

// ── Helper: call Gemini API ──────────────────────────────────────────────────
async function callGemini(systemPrompt, userPrompt) {
  if (!GEMINI_API_KEY) throw new Error("REACT_APP_GEMINI_API_KEY belum diset di .env");

  const response = await fetch(GEMINI_API_URL(GEMINI_API_KEY), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      system_instruction: { parts: [{ text: systemPrompt }] },
      contents: [{ role: "user", parts: [{ text: userPrompt }] }],
      generationConfig: {
        maxOutputTokens: 1024,
        temperature: 0.7,
      },
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const msg = err?.error?.message || `Gemini API error ${response.status}`;
    throw new Error(msg);
  }

  const data = await response.json();
  return data.candidates?.[0]?.content?.parts?.[0]?.text?.trim() || "";
}

// GANTI_KE_CLAUDE: Uncomment blok di bawah & hapus callGemini di atas
// const CLAUDE_API_URL = "https://api.anthropic.com/v1/messages";
// async function callClaude(systemPrompt, userPrompt) {
//   const response = await fetch(CLAUDE_API_URL, {
//     method: "POST",
//     headers: { "Content-Type": "application/json" },
//     body: JSON.stringify({
//       model: "claude-sonnet-4-20250514",
//       max_tokens: 1000,
//       system: systemPrompt,
//       messages: [{ role: "user", content: userPrompt }],
//     }),
//   });
//   if (!response.ok) { const err = await response.json().catch(()=>({})); throw new Error(err?.error?.message || `API error ${response.status}`); }
//   const data = await response.json();
//   return data.content?.map(b => b.type === "text" ? b.text : "").join("").trim() || "";
// }

// ─── Helper ──────────────────────────────────────────────────────────────────

function avg(arr, key) {
  const vals = arr.map((s) => s[key]).filter((v) => v != null && !isNaN(v) && v > 0);
  return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
}

function calcCardioEfficiency(sessions) {
  // Efisiensi kardio: HR rendah di speed tinggi = makin fit
  const road = sessions.filter(
    (s) => s.day_type?.toLowerCase().includes("road") || s.day_type?.toLowerCase().includes("long")
  );
  if (road.length < 3) return null;
  const recent3 = road.slice(0, 3);
  const older3 = road.slice(-3);
  const recentRatio = avg(recent3, "heart_rate") / avg(recent3, "avg_speed");
  const olderRatio = avg(older3, "heart_rate") / avg(older3, "avg_speed");
  if (!recentRatio || !olderRatio) return null;
  const delta = (((olderRatio - recentRatio) / olderRatio) * 100).toFixed(1);
  return { recentRatio: recentRatio.toFixed(2), olderRatio: olderRatio.toFixed(2), delta };
}

function calcEffortHonesty(sessions) {
  // Kejujuran usaha: bandingkan RPE vs HR — RPE tinggi tapi HR rendah = under-report
  const valid = sessions.filter((s) => s.rpe && s.heart_rate);
  if (valid.length < 5) return null;
  const discrepancies = valid.map((s) => {
    const expectedHr = 60 + s.rpe * 15; // estimasi kasar: RPE 10 ~ 210bpm
    return { date: s.session_date, rpe: s.rpe, hr: s.heart_rate, diff: s.heart_rate - expectedHr };
  });
  const avgDiff = (discrepancies.reduce((a, b) => a + b.diff, 0) / discrepancies.length).toFixed(1);
  const outliers = discrepancies.filter((d) => Math.abs(d.diff) > 25);
  return { avgDiff, outlierCount: outliers.length, outliers: outliers.slice(0, 3) };
}

function calcFitnessTrend(sessions) {
  // Tren kebugaran: speed minggu ini vs 2-3 minggu lalu (road sesi saja)
  const road = sessions.filter(
    (s) =>
      s.day_type?.toLowerCase().includes("road") ||
      s.day_type?.toLowerCase().includes("long")
  );
  if (road.length < 4) return null;
  const half = Math.floor(road.length / 2);
  const recentAvgSpeed = avg(road.slice(0, half), "avg_speed");
  const olderAvgSpeed = avg(road.slice(half), "avg_speed");
  if (!recentAvgSpeed || !olderAvgSpeed) return null;
  const delta = (recentAvgSpeed - olderAvgSpeed).toFixed(1);
  return { recentAvgSpeed, olderAvgSpeed, delta };
}

function calcOvertrainingRisk(sessions, hrRest) {
  // Risiko overtraining: HR istirahat naik + performa drop
  const last5 = sessions.slice(0, 5);
  const prev5 = sessions.slice(5, 10);
  if (last5.length < 3 || prev5.length < 3) return null;
  const recentHr = avg(last5, "heart_rate");
  const olderHr = avg(prev5, "heart_rate");
  const recentRpe = avg(last5, "rpe");
  const olderRpe = avg(prev5, "rpe");
  const recentSpeed = avg(last5, "avg_speed");
  const olderSpeed = avg(prev5, "avg_speed");
  const hrTrend = recentHr && olderHr ? (recentHr - olderHr).toFixed(1) : null;
  const rpeTrend = recentRpe && olderRpe ? (recentRpe - olderRpe).toFixed(1) : null;
  const speedTrend = recentSpeed && olderSpeed ? (recentSpeed - olderSpeed).toFixed(1) : null;
  // Risk signal: HR naik + speed turun
  const risk =
    hrTrend > 3 && speedTrend < -0.5
      ? "tinggi"
      : hrTrend > 2 || (rpeTrend > 1 && speedTrend < 0)
      ? "sedang"
      : "rendah";
  return { hrTrend, rpeTrend, speedTrend, risk, hrRest };
}

function calcHillQuality(hillSessions) {
  // Hill repeat quality: konsistensi per rep, degradasi wajar vs terlalu tajam
  if (!hillSessions || hillSessions.length < 3) return null;
  // Kelompokkan per session_log_id
  const grouped = {};
  hillSessions.forEach((h) => {
    if (!grouped[h.session_log_id]) grouped[h.session_log_id] = [];
    grouped[h.session_log_id].push(h);
  });
  const sessions = Object.values(grouped).filter((g) => g.length >= 2);
  if (sessions.length === 0) return null;
  const analyses = sessions.map((reps) => {
    const sorted = reps.sort((a, b) => a.rep_number - b.rep_number);
    const durations = sorted.map((r) => r.duration_seconds);
    const first = durations[0];
    const last = durations[durations.length - 1];
    const degradation = (((last - first) / first) * 100).toFixed(1);
    return { repCount: reps.length, firstRep: first, lastRep: last, degradation };
  });
  const avgDegradation = (
    analyses.reduce((a, b) => a + parseFloat(b.degradation), 0) / analyses.length
  ).toFixed(1);
  return { sessions: analyses, avgDegradation };
}

function calcTeamPosition(sessions, teamAverages, category) {
  // Posisi relatif vs rata-rata kategori
  if (!teamAverages) return null;
  const mySpeed = avg(sessions.slice(0, 10), "avg_speed");
  const myCadence = avg(sessions.slice(0, 10), "cadence");
  const myHr = avg(sessions.slice(0, 10), "heart_rate");
  if (!mySpeed || !teamAverages.avg_speed) return null;
  const speedPct = (((mySpeed - teamAverages.avg_speed) / teamAverages.avg_speed) * 100).toFixed(1);
  const cadencePct =
    myCadence && teamAverages.cadence
      ? (((myCadence - teamAverages.cadence) / teamAverages.cadence) * 100).toFixed(1)
      : null;
  return { mySpeed, teamSpeed: teamAverages.avg_speed, speedPct, myCadence, teamCadence: teamAverages.cadence, cadencePct, category };
}

// ─── Build context untuk analyzeAthlete ──────────────────────────────────────

function buildAthleteContext(athlete, sessions, coachNotes = [], hillLogs = [], badges = [], teamAverages = null, role = "coach") {
  const recentSessions = sessions.slice(0, 30);
  const hrRest = athlete?.hr_rest;

  // Hitung 6 metrik
  const cardio = calcCardioEfficiency(recentSessions);
  const honesty = calcEffortHonesty(recentSessions);
  const fitness = calcFitnessTrend(recentSessions);
  const overtraining = calcOvertrainingRisk(recentSessions, hrRest);
  const hillQuality = calcHillQuality(hillLogs);
  const teamPos = calcTeamPosition(recentSessions, teamAverages, athlete?.category);

  const sessionSummary = recentSessions
    .map((s, i) => {
      const hills = hillLogs.filter((h) => h.session_log_id === s.id);
      const hillStr =
        hills.length > 0
          ? ` | Hill ${hills.length} rep: ${hills.map((h) => `${h.rep_number}=${h.duration_seconds}d`).join("/")}`
          : "";
      return [
        `Sesi ${i + 1}: ${s.session_date || "?"} | ${s.day_type || "?"} ${s.mode ? "— " + s.mode : ""}`,
        `  RPE: ${s.rpe ?? "-"}, Speed: ${s.avg_speed ?? "-"} km/j, Cadence: ${s.cadence ?? "-"} rpm`,
        `  HR: ${s.heart_rate ?? "-"} bpm, Jarak: ${s.distance ?? "-"} km, Durasi: ${s.duration ?? "-"} mnt${s.power_watts ? `, Power: ${s.power_watts}w` : ""}${hillStr}`,
      ].join("\n");
    })
    .join("\n\n");

  const notesSummary =
    coachNotes.length > 0
      ? coachNotes.slice(0, 5).map((n) => `- ${n.created_at?.slice(0, 10) || "?"}: "${n.note}"`).join("\n")
      : "Belum ada catatan coach.";

  const badgeList =
    badges.length > 0
      ? badges.map((b) => b.badge_name || b.name || b.badge?.name).filter(Boolean).join(", ")
      : "Belum ada badge.";

  const athleteProfile = athlete
    ? [
        `Nama: ${athlete.full_name || athlete.name || "Atlet"}`,
        `Kategori: ${athlete.category || "belum diset"}`,
        `Berat: ${athlete.weight_kg ?? "-"} kg, Tinggi: ${athlete.height_cm ?? "-"} cm`,
        `HR Istirahat: ${hrRest ?? "-"} bpm`,
        `Pengalaman: ${athlete.cycling_exp_years ?? "-"} tahun`,
        `Badge: ${badgeList}`,
      ].join("\n")
    : "Profil tidak tersedia.";

  const metricsContext = [
    cardio ? `EFISIENSI KARDIO: Rasio HR/Speed terkini=${cardio.recentRatio} vs sebelumnya=${cardio.olderRatio} (delta ${cardio.delta}% — ${parseFloat(cardio.delta) > 0 ? "MEMBAIK" : "MENURUN"})` : null,
    honesty ? `KEJUJURAN EFFORT: Rata-rata selisih HR vs RPE-ekspektasi=${honesty.avgDiff} bpm, ${honesty.outlierCount} sesi anomali besar` : null,
    fitness ? `TREN KEBUGARAN: Speed terkini avg=${fitness.recentAvgSpeed} km/j vs sebelumnya=${fitness.olderAvgSpeed} km/j (delta ${fitness.delta > 0 ? "+" : ""}${fitness.delta} km/j)` : null,
    overtraining ? `RISIKO OVERTRAINING: HR trend=${overtraining.hrTrend > 0 ? "+" : ""}${overtraining.hrTrend} bpm, Speed trend=${overtraining.speedTrend} km/j, RPE trend=${overtraining.rpeTrend} → RISIKO ${overtraining.risk.toUpperCase()}` : null,
    hillQuality ? `HILL QUALITY: Avg degradasi per sesi=${hillQuality.avgDegradation}% (rep pertama vs terakhir). ${parseFloat(hillQuality.avgDegradation) > 15 ? "Degradasi terlalu tajam — perlu perhatian." : "Dalam batas wajar."}` : null,
    teamPos ? `POSISI TIM (kategori ${teamPos.category}): Speed atlet=${teamPos.mySpeed} km/j vs rata-rata=${teamPos.teamSpeed} km/j (${teamPos.speedPct > 0 ? "+" : ""}${teamPos.speedPct}%)${teamPos.cadencePct ? `, Cadence ${teamPos.cadencePct > 0 ? "+" : ""}${teamPos.cadencePct}% vs rata-rata` : ""}` : null,
  ]
    .filter(Boolean)
    .join("\n");

  // ── System prompt per role ─────────────────────────────────────────────────
  const systemByRole = {
    coach: `Kamu adalah analis performa atletik berpengalaman yang membantu coach tim sepeda remaja Rollaco Cycling Academy.
Berikan analisa mendalam, jujur, dan actionable. Gunakan terminologi olahraga yang tepat tapi tidak berlebihan.
Highlight insight yang tidak terlihat dari angka biasa. Jangan hanya merangkum — interpretasi dan rekomendasikan.
Perhatikan 6 metrik yang sudah dihitung: efisiensi kardio, kejujuran effort, tren kebugaran, risiko overtraining, hill quality, posisi relatif tim.
Bahasa Indonesia yang natural dan profesional, bukan kaku.`,

    admin: `Kamu adalah analis data performa atletik untuk manajemen Rollaco Cycling Academy.
Berikan perspektif holistik dari sisi pengembangan jangka panjang atlet.
Sertakan potensi, area kritis, dan rekomendasi program berdasarkan 6 metrik analisis yang tersedia.
Bahasa Indonesia yang natural dan profesional.`,

    parent: `Kamu adalah asisten yang membantu orang tua memahami perkembangan anak di klub sepeda remaja Rollaco Cycling Academy.
Gunakan bahasa yang hangat, sederhana, dan mudah dipahami — HINDARI jargon teknis seperti "HR/Speed ratio", "cadence degradation", dll.
Terjemahkan angka menjadi cerita yang bermakna: "anak Anda semakin efisien" bukan "rasio HR/speed turun 5%".
Fokus pada kemajuan positif, area yang perlu perhatian orang tua, dan hal yang bisa didukung dari rumah.
Jujur tapi tidak menakutkan. Bahasa Indonesia yang hangat dan suportif.`,
  };

  // ── Output format per role ─────────────────────────────────────────────────
  const outputFormatByRole = {
    coach: `## Kondisi Terkini
## Temuan Kunci (dari 6 Metrik)
## Sinyal Overtraining / Kelelahan
## Rekomendasi Sesi Berikutnya`,

    admin: `## Ringkasan Performa
## Kekuatan & Potensi
## Area Perhatian
## Rekomendasi Program`,

    parent: `## Kabar Terbaru Anak Anda
## Yang Berkembang Pesat
## Yang Perlu Perhatian Bersama
## Cara Mendukung dari Rumah`,
  };

  const userPrompt = `Berikut data atlet yang perlu dianalisa oleh RollBOT:

=== PROFIL ATLET ===
${athleteProfile}

=== HASIL KALKULASI 6 METRIK ===
${metricsContext || "Data tidak cukup untuk kalkulasi metrik."}

=== DATA ${recentSessions.length} SESI TERAKHIR ===
${sessionSummary || "Belum ada data sesi."}

=== CATATAN COACH ===
${notesSummary}

Format output (gunakan heading ## persis seperti ini):
${outputFormatByRole[role] || outputFormatByRole.coach}

Berikan analisa yang tajam dan personal — bukan template kaku.
Sebut angka aktual dari data jika relevan. Maksimal 350 kata.
Jika data terbatas, tetap berikan analisa terbaik dan sebutkan keterbatasannya secara singkat.
${role === "parent" ? "PENTING: JANGAN gunakan istilah teknis seperti RPE, bpm, cadence, HR. Terjemahkan ke bahasa sehari-hari." : ""}`;

  return {
    systemPrompt: systemByRole[role] || systemByRole.coach,
    userPrompt,
    metrics: { cardio, honesty, fitness, overtraining, hillQuality, teamPos },
  };
}

// ─── Export: analyzeAthlete ───────────────────────────────────────────────────

export async function analyzeAthlete({
  athlete,
  sessions,
  coachNotes = [],
  hillLogs = [],
  badges = [],
  teamAverages = null,
  role = "coach",
}) {
  const { systemPrompt, userPrompt, metrics } = buildAthleteContext(
    athlete,
    sessions,
    coachNotes,
    hillLogs,
    badges,
    teamAverages,
    role
  );

  const text = await callGemini(systemPrompt, userPrompt);
  return { text, metrics };
}

// ─── Build context untuk Laporan Mingguan ────────────────────────────────────

function buildWeeklyContext(athlete, sessions, coachNotes, role) {
  const now = new Date();
  const weekAgo = new Date(now - 7 * 24 * 3600 * 1000);
  const prevWeekAgo = new Date(now - 14 * 24 * 3600 * 1000);

  const thisWeek = sessions.filter(
    (s) => s.session_date && new Date(s.session_date) >= weekAgo
  );
  const lastWeek = sessions.filter((s) => {
    const d = s.session_date && new Date(s.session_date);
    return d && d >= prevWeekAgo && d < weekAgo;
  });

  const thisStats = {
    sessions: thisWeek.length,
    avgRpe: avg(thisWeek, "rpe"),
    avgHr: avg(thisWeek, "heart_rate"),
    avgSpeed: avg(thisWeek, "avg_speed"),
    avgCadence: avg(thisWeek, "cadence"),
    totalDistance: thisWeek.reduce((a, s) => a + (s.distance || 0), 0).toFixed(1),
    totalDuration: thisWeek.reduce((a, s) => a + (s.duration || 0), 0),
  };

  const lastStats = {
    sessions: lastWeek.length,
    avgRpe: avg(lastWeek, "rpe"),
    avgHr: avg(lastWeek, "heart_rate"),
    avgSpeed: avg(lastWeek, "avg_speed"),
  };

  const notesSummary = coachNotes
    .filter((n) => new Date(n.created_at) >= weekAgo)
    .map((n) => `- "${n.note}" (${n.created_at?.slice(0, 10)})`)
    .join("\n") || "Tidak ada catatan coach minggu ini.";

  const systemByRole = {
    coach: `Kamu adalah analis performa sepeda berpengalaman. Buat laporan mingguan yang tajam dan actionable untuk coach. 
Gunakan bahasa Indonesia profesional. Bandingkan data minggu ini vs minggu lalu. Highlight anomali dan tren penting.`,
    admin: `Kamu adalah analis performa untuk manajemen klub sepeda. Buat laporan mingguan komprehensif untuk admin. 
Fokus pada perkembangan jangka panjang dan rekomendasi program. Bahasa Indonesia profesional.`,
    parent: `Kamu adalah asisten yang menjelaskan perkembangan atlet sepeda remaja kepada orang tuanya. 
Gunakan bahasa Indonesia yang hangat, positif, dan mudah dipahami — hindari jargon teknis seperti RPE, bpm, cadence.
Terjemahkan angka ke cerita yang bermakna. Sampaikan dengan cara yang membuat orang tua merasa tenang dan bangga.`,
  };

  const userPrompt = `Buat laporan mingguan ${athlete?.full_name || "atlet"} berdasarkan data berikut:

MINGGU INI (7 hari terakhir):
- Jumlah sesi: ${thisStats.sessions}
- Total jarak: ${thisStats.totalDistance} km
- Total durasi: ${Math.round(thisStats.totalDuration / 60)} jam ${thisStats.totalDuration % 60} menit
- Rata-rata RPE: ${thisStats.avgRpe || "–"}
- Rata-rata HR: ${thisStats.avgHr || "–"} bpm
- Rata-rata Speed: ${thisStats.avgSpeed || "–"} km/j
- Rata-rata Cadence: ${thisStats.avgCadence || "–"} rpm

MINGGU LALU (perbandingan):
- Jumlah sesi: ${lastStats.sessions}
- Rata-rata RPE: ${lastStats.avgRpe || "–"}
- Rata-rata HR: ${lastStats.avgHr || "–"} bpm
- Rata-rata Speed: ${lastStats.avgSpeed || "–"} km/j

CATATAN COACH MINGGU INI:
${notesSummary}

Sesi detail minggu ini:
${
  thisWeek
    .map(
      (s, i) =>
        `${i + 1}. ${s.session_date} | ${s.day_type || "?"} ${s.mode ? "— " + s.mode : ""} | RPE ${s.rpe ?? "–"} | HR ${s.heart_rate ?? "–"} bpm | ${s.avg_speed ?? "–"} km/j`
    )
    .join("\n") || "Tidak ada sesi minggu ini."
}

Format output (gunakan heading ## ):
## Ringkasan Minggu Ini
## Perubahan vs Minggu Lalu
## Momen Terbaik
## Yang Perlu Diperhatikan
## Rekomendasi Minggu Depan

Tulis dengan narasi yang hidup dan personal — bukan template kaku. Sebut angka spesifik. Maksimal 350 kata.
${role === "parent" ? "PENTING: Gunakan bahasa orang tua, bukan bahasa pelatih. Jangan sebut RPE, bpm, cadence secara langsung." : ""}`;

  return { systemPrompt: systemByRole[role] || systemByRole.coach, userPrompt };
}

export async function generateWeeklyReport({ athlete, sessions, coachNotes = [], role = "coach" }) {
  const { systemPrompt, userPrompt } = buildWeeklyContext(athlete, sessions, coachNotes, role);
  return await callGemini(systemPrompt, userPrompt);
}

